<template>
    <!--
        Horizontal dark navigation bar — variant A's chrome anchor.
        Logo on the left, nav items in the middle, search + bell +
        user info on the right. Uses --nav-* tokens from tokens.css.
    -->
    <div class="tag-topnav" :style="rootStyle">
        <!-- Brand block -->
        <div :style="brandStyle">
            <span class="tag-logo-mark" :style="{ width: '24px', height: '24px', flex: '0 0 24px' }" />
            <span :style="brandTextStyle">TAG</span>
        </div>

        <!-- Main nav -->
        <nav :style="navStyle">
            <a
                v-for="n in items"
                :key="n.id"
                :href="n.href"
                :style="navItemStyle(n.id === activeId)"
            >
                <span :style="{ opacity: 0.85, display: 'inline-flex' }">
                    <Icon :name="n.icon" />
                </span>
                {{ n.label }}
                <span v-if="n.count != null" :style="navCountStyle">{{ n.count }}</span>
            </a>
        </nav>

        <!-- Right side: search + bell + user -->
        <div :style="rightStyle">
            <button type="button" class="btn btn--sm" :style="searchBtnStyle">
                <span style="opacity:0.8; display:inline-flex">
                    <Icon name="search" />
                </span>
                <span>Search…</span>
                <span :style="kbdGroupStyle">
                    <span class="kbd" :style="kbdInvStyle">⌘</span>
                    <span class="kbd" :style="kbdInvStyle">K</span>
                </span>
            </button>
            <button
                type="button"
                class="btn btn--icon btn--sm"
                :style="bellBtnStyle"
                aria-label="Notifications"
            >
                <Icon name="bell" />
            </button>
            <div :style="userBlockStyle">
                <Avatar :name="userName" :size="26" />
                <div :style="{ lineHeight: 1.1 }">
                    <div :style="{ fontSize: '12px', fontWeight: 500 }">{{ userShortName }}</div>
                    <div :style="userEmailStyle">{{ userEmail }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from "./Icon.vue";
import Avatar from "./Avatar.vue";

const DEFAULT_ITEMS = [
    { id: "agencies", label: "Agencies",        icon: "building",  href: "/agencies" },
    { id: "deals",    label: "Deals",           icon: "briefcase", href: "/deals" },
    { id: "sales",    label: "Sales",           icon: "trending",  href: "/sales" },
    { id: "invoices", label: "Master invoices", icon: "receipt",   href: "/master-invoices" },
    { id: "brands",   label: "Brands",          icon: "spark",     href: "/brands" },
    { id: "partners", label: "Partners",        icon: "user",      href: "/partners" },
    { id: "admin",    label: "Admin",           icon: "shield",    href: "/admin" },
];

export default {
    name: "TopNav",
    components: { Icon, Avatar },
    props: {
        activeId: { type: String, required: true },
        items: { type: Array, default: () => DEFAULT_ITEMS },
        userName: { type: String, default: "Admin User" },
        userEmail: { type: String, default: "admin@tag.co" },
    },
    computed: {
        userShortName() {
            return (this.userName || "").split(/\s+/)[0] || this.userName;
        },
        rootStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: 0,
                height: "52px",
                padding: "0 20px",
                background: "var(--nav-bg)",
                color: "var(--nav-text)",
                borderBottom: "1px solid color-mix(in oklch, var(--nav-bg) 70%, black)",
                flex: "0 0 auto",
            };
        },
        brandStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "12px",
                paddingRight: "24px",
                marginRight: "8px",
            };
        },
        brandTextStyle() {
            return {
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                letterSpacing: "-0.01em",
                fontSize: "14px",
            };
        },
        navStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "2px",
                flex: "1 1 auto",
            };
        },
        navCountStyle() {
            return {
                fontSize: "10.5px",
                fontFamily: "var(--font-mono)",
                fontWeight: 500,
                color: "var(--nav-muted)",
                marginLeft: "2px",
            };
        },
        rightStyle() {
            return { display: "flex", alignItems: "center", gap: "8px" };
        },
        searchBtnStyle() {
            return {
                background: "var(--nav-hover)",
                borderColor: "transparent",
                color: "var(--nav-muted)",
                gap: "8px",
                minWidth: "220px",
                justifyContent: "flex-start",
            };
        },
        kbdGroupStyle() {
            return {
                marginLeft: "auto",
                display: "inline-flex",
                gap: "2px",
            };
        },
        kbdInvStyle() {
            return {
                background: "transparent",
                borderColor: "color-mix(in oklch, var(--nav-text) 20%, transparent)",
                color: "var(--nav-muted)",
            };
        },
        bellBtnStyle() {
            return {
                background: "transparent",
                borderColor: "transparent",
                color: "var(--nav-muted)",
            };
        },
        userBlockStyle() {
            return {
                display: "flex",
                alignItems: "center",
                gap: "8px",
                paddingLeft: "8px",
                marginLeft: "4px",
                borderLeft: "1px solid color-mix(in oklch, var(--nav-text) 14%, transparent)",
            };
        },
        userEmailStyle() {
            return {
                fontSize: "10.5px",
                color: "var(--nav-muted)",
            };
        },
    },
    methods: {
        navItemStyle(active) {
            return {
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                padding: "7px 12px",
                borderRadius: "7px",
                fontSize: "13px",
                fontWeight: 500,
                letterSpacing: "-0.005em",
                color: active ? "var(--nav-text)" : "var(--nav-muted)",
                background: active ? "var(--nav-hover)" : "transparent",
                textDecoration: "none",
                position: "relative",
            };
        },
    },
};
</script>
